/**
 * ScenarioDashboard.tsx
 * FEAT-2026-0007 Phase 6F.5 — Scenario list / dashboard.
 *
 * Rendered by ScenarioPlanningPage when no scenario is active and no deep-link
 * params are present.  Replaces the bare ScenarioSourcePicker in that state.
 *
 * Features:
 *   - Table listing all non-BASELINE, non-deleted scenarios (newest first)
 *   - Per-row: Open, Duplicate, Delete (disabled for Submitted)
 *   - "+ New Scenario" button that shows the inline source picker
 *   - Refresh (reload list) after mutating actions
 *   - ConfirmDialog for delete with cascade counts in snackbar
 *   - Status chips via theme.schedule.* tokens (no hex literals)
 *
 * Open action: calls onOpenScenario(key) — parent fetches getScenario(key) and
 * sets it as the active scenario, resuming the wizard at Phase 1.
 *
 * Duplicate action: calls POST /api/scenarios/:key/duplicate, then opens the
 * new copy immediately (same flow as "Open").
 *
 * Create new: toggles an inline ScenarioSourcePicker panel.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../../components/shared/ConfirmDialog';
import type { Scenario, ScenarioStatus } from '../../../../services/scheduleScenario';
import {
  listScenarios,
  deleteScenario,
  duplicateScenario,
  getScenario,
  type ScenarioListItem,
} from '../../../../services/scheduleScenario';
import { ScenarioSourcePicker } from './ScenarioSourcePicker';
import { sourceLines } from './scenarioSourceLabel';
import { usePermissions } from '../../../../hooks/usePermissions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScenarioDashboardProps {
  /** Called when the user opens or duplicates a scenario to edit it. */
  onOpenScenario: (scenario: Scenario) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// 2026-07-10 scenario-naming fix: rows used to show only "Quote (…9065)" — a
// plan for the quote "Terrane" was unfindable. The source identity now renders
// via the shared sourceLines() helper (scenarioSourceLabel.ts), also used by
// the locked-plan dialog.

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

function StatusChip({ status }: { status: ScenarioStatus }) {
  const theme = useTheme();

  let bgColor: string;
  let textColor: string;
  let borderColor: string;

  switch (status) {
    case 'Draft':
      bgColor    = alpha(theme.schedule.tentative, 0.18);
      textColor  = theme.schedule.committed;
      borderColor = alpha(theme.schedule.committed, 0.3);
      break;
    case 'NeedsReview':
      bgColor    = alpha(theme.schedule.maintenance, 0.18);
      textColor  = theme.schedule.maintenance;
      borderColor = alpha(theme.schedule.maintenance, 0.4);
      break;
    case 'Submitted':
      bgColor    = alpha(theme.schedule.employeeAlloc, 0.18);
      textColor  = theme.schedule.employeeAlloc;
      borderColor = alpha(theme.schedule.employeeAlloc, 0.4);
      break;
    case 'Archived':
    default:
      bgColor    = alpha(theme.schedule.noActivity, 0.18);
      textColor  = theme.schedule.noActivity;
      borderColor = alpha(theme.schedule.noActivity, 0.4);
      break;
  }

  const label = status === 'NeedsReview' ? 'Needs Review' : status;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 20,
        backgroundColor: bgColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScenarioDashboard: React.FC<ScenarioDashboardProps> = ({
  onOpenScenario,
}) => {
  const theme = useTheme();
  // Scenario delete is admin-only (matches backend DELETE /scenarios/:key, RBAC audit D3)
  const { isAdmin } = usePermissions();

  // ── List state ──────────────────────────────────────────────────────────
  const [items, setItems] = useState<ScenarioListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ── Create panel toggle ──────────────────────────────────────────────────
  const [showPicker, setShowPicker] = useState(false);

  // ── Per-row loading guards ───────────────────────────────────────────────
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [duplicatingKey, setDuplicatingKey] = useState<string | null>(null);

  // ── Confirm dialog ───────────────────────────────────────────────────────
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // ── Snackbar ─────────────────────────────────────────────────────────────
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'error';
  }>({ open: false, message: '', severity: 'info' });

  const showSnack = (message: string, severity: 'success' | 'info' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Load list ────────────────────────────────────────────────────────────
  const loadList = useCallback(() => {
    setLoading(true);
    setListError(null);
    listScenarios()
      .then(setItems)
      .catch(() => setListError('Failed to load scenarios. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ── Open handler ──────────────────────────────────────────────────────────
  const handleOpen = async (key: string) => {
    if (openingKey) return;
    setOpeningKey(key);
    try {
      const scenario = await getScenario(key);
      onOpenScenario(scenario);
    } catch {
      showSnack('Failed to load scenario. Please try again.', 'error');
    } finally {
      setOpeningKey(null);
    }
  };

  // ── Duplicate handler ─────────────────────────────────────────────────────
  const handleDuplicate = async (key: string, codeName: string) => {
    if (duplicatingKey) return;
    setDuplicatingKey(key);
    try {
      const result = await duplicateScenario(key);
      showSnack(`Duplicated as ${result.scenario.Code}`);
      // Open the new copy for immediate editing
      onOpenScenario(result.scenario);
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      showSnack(msg ?? `Failed to duplicate ${codeName}.`, 'error');
      setDuplicatingKey(null);
    }
  };

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleDeleteClick = (item: ScenarioListItem) => {
    setConfirm({
      open: true,
      title: 'Delete Draft Scenario?',
      message: `This will permanently delete ${item.Code} — "${item.Name}" and release all its tentative bookings and assignments. This action cannot be undone.`,
      confirmLabel: 'Delete Draft',
      confirmColor: 'error',
      onConfirm: () => {
        deleteScenario(item.ScenarioKey)
          .then((result) => {
            const parts: string[] = [];
            if (result.bookingsDeleted > 0) {
              parts.push(`${result.bookingsDeleted} booking${result.bookingsDeleted !== 1 ? 's' : ''}`);
            }
            if (result.assignmentsDeleted > 0) {
              parts.push(`${result.assignmentsDeleted} assignment${result.assignmentsDeleted !== 1 ? 's' : ''}`);
            }
            const countText = parts.length > 0 ? ` — ${parts.join(' and ')} released` : '';
            showSnack(`Draft deleted${countText}.`);
            loadList();
          })
          .catch((err: unknown) => {
            const msg =
              err &&
              typeof err === 'object' &&
              'response' in err
                ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
                : undefined;
            showSnack(msg ?? 'Failed to delete scenario.', 'error');
          });
      },
    });
  };

  // ── Scenario created via picker ───────────────────────────────────────────
  const handlePickerReady = (scenario: Scenario) => {
    setShowPicker(false);
    onOpenScenario(scenario);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── Title bar ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ flex: 1 }}>
          Scenario Plans
        </Typography>
        <Tooltip title="Refresh the list">
          <IconButton size="small" onClick={loadList} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setShowPicker((v) => !v)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {showPicker ? 'Cancel' : '+ New Scenario'}
        </Button>
      </Box>

      {/* ── Inline source picker (create new) ──────────────────────────── */}
      <Collapse in={showPicker} unmountOnExit>
        <Box sx={{ mb: 1 }}>
          <ScenarioSourcePicker onScenarioReady={handlePickerReady} />
        </Box>
      </Collapse>

      <Divider />

      {/* ── List error ─────────────────────────────────────────────────── */}
      {listError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {listError}
        </Alert>
      )}

      {/* ── Loading spinner ─────────────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {!loading && !listError && items.length === 0 && (
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            No scenarios yet.
          </Typography>
          <Typography variant="body2">
            Click "+ New Scenario" above to create your first scenario plan.
          </Typography>
        </Box>
      )}

      {/* ── Scenario table ────────────────────────────────────────────────── */}
      {!loading && items.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: alpha(theme.schedule.committed, 0.05),
                }}
              >
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Code</TableCell>
                {/* 2026-07-10: source identity FIRST — it's how planners find a plan */}
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Quote / Work Order</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Plan Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'right' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const isSubmitted = item.Status === 'Submitted' || item.Status === 'Archived';
                const isOpening   = openingKey === item.ScenarioKey;
                const isDuplicating = duplicatingKey === item.ScenarioKey;

                return (
                  <TableRow
                    key={item.ScenarioKey}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {item.Code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const src = sourceLines(item);
                        return (
                          <>
                            <Typography variant="body2" fontWeight={600} color="text.primary">
                              {src.primary}
                            </Typography>
                            {src.secondary && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {src.secondary}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {item.Name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={item.Status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        {formatDate(item.sysCreateDate)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Open */}
                      <Tooltip title="Open this scenario in the planning wizard" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => { void handleOpen(item.ScenarioKey); }}
                            disabled={isOpening || isDuplicating}
                            sx={{ color: theme.schedule.committed }}
                          >
                            {isOpening
                              ? <CircularProgress size={14} color="inherit" />
                              : <OpenInBrowserIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Duplicate */}
                      <Tooltip title="Duplicate — creates a new Draft copy of this scenario" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => { void handleDuplicate(item.ScenarioKey, item.Code); }}
                            disabled={isOpening || isDuplicating}
                            sx={{ color: theme.schedule.machineTime }}
                          >
                            {isDuplicating
                              ? <CircularProgress size={14} color="inherit" />
                              : <ContentCopyIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Delete */}
                      <Tooltip
                        title={
                          !isAdmin
                            ? 'Deleting scenarios requires a Lab Admin'
                            : isSubmitted
                              ? 'Submitted / Archived scenarios cannot be deleted'
                              : 'Delete this draft scenario and release its bookings'
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(item)}
                            disabled={!isAdmin || isSubmitted || isOpening || isDuplicating}
                            sx={{
                              color: isSubmitted || !isAdmin
                                ? alpha(theme.palette.error.main, 0.3)
                                : theme.palette.error.main,
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Confirm dialog ───────────────────────────────────────────────── */}
      <ConfirmDialog
        state={confirm}
        onClose={() => setConfirm((s) => ({ ...s, open: false }))}
      />

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
